<template>
    <AddFileSection
        :id="AddFileFormSectionIds.REQUEST_WIRE_INFORMATION"
        sectionDescription="Select clients to request wire information for this file."
        sectionHeading="Request wire information"
    >
        <ClientMultiSelect
            class="mt-3"
            :clientOptions
            :error="requestBankInfoRecipientsError"
            @update:modelValue="onClientsUpdate"
        />
    </AddFileSection>
</template>

<script setup lang="ts">
import { ClientSelectionErrorKeys } from '@/company-portal/add-file/add-file-form-errors.types';
import { useAddFileStore } from '@/company-portal/add-file/add-file.store';
import { AddFileFormSectionIds } from '@/company-portal/add-file/add-file.types';
import { useAddFileError } from '@/company-portal/add-file/use-add-file-error';
import AddFileSection from '@/components/Views/Company/AddFile/AddFileSection.vue';
import ClientMultiSelect from '@/components/Views/Company/AddFile/ClientMultiSelect.vue';
import type { ClientOption, ClientType } from '@/components/Views/Company/File/file.types';
import { joinFirstLast } from '@/shared/utils/strings';
import { useFileClientStore } from '@/stores/company/file-client.store';
import { computed } from 'vue';

const addFileStore = useAddFileStore();
const fileClientStore = useFileClientStore();

const { serverValidationErrors, clearServerValidationError } = useAddFileError();

const clientOptions = computed<ClientOption[]>(() =>
    fileClientStore.availableClientOptions.map(client => {
        const isWireDocRecipient = addFileStore.wireDocRecipientClientIds.includes(client.id);

        return {
            id: client.id,
            value: client.id,
            email: client.email,
            first: client.first,
            last: client.last,
            label: joinFirstLast(client.first, client.last),
            phone: client.phone,
            /** @todo cleanup in [CLENG-883] assertion is needed because we have duplicate type declarations for client role  */
            role: client.role as ClientType,
            disabled: isWireDocRecipient,
            ...(isWireDocRecipient && { helperText: 'This client is already selected to be sent wire instructions.' }),
        };
    }),
);

const onClientsUpdate = (clients: ClientOption[]) => {
    addFileStore.requestWireInfoClientIds = clients.map(client => client.id);
    clearServerValidationError(ClientSelectionErrorKeys.REQUEST_BANK_INFO_RECIPIENTS);
};

const requestBankInfoRecipientsError = computed(() =>
    serverValidationErrors.value?.[ClientSelectionErrorKeys.REQUEST_BANK_INFO_RECIPIENTS]?.join(' '),
);
</script>
